import {
  PutStudentBasicInfoRequest,
  StudentProfile,
} from '../../../../constants/types/student/profile/type';
import {
  DEFAULT_COUNTRY_CODE,
  normalizePhone,
  toCountryCodeValue,
} from '../../../../constants/validation';

/**
 * 학생 내 정보 화면의 폼 값.
 *
 * 카드(기본 정보 / 보호자 정보)마다 저장 API가 다르고 서로 겹치는 필드도 없어서
 * 카드별로 폼을 따로 가진다. 그래서 폼 타입과 변환도 카드 단위로 나눠 둔다.
 *
 * LMS 학생 상세는 입력 컴포넌트부터 다르므로 폼 타입도
 * `constants/types/student-profile-form`의 StudentProfileForm으로 따로 둔다.
 */

//============================== 기본 정보 ==============================

/** 기본 정보 카드의 폼 값. (아이디/이름은 수정할 수 없어 폼에서 다루지 않는다) */
export type StudentBasicInfoForm = {
  englishName: string | null;
  birthday: string | null;
  gender: string | null;
};

/** 조회 응답을 기본 정보 폼 값으로 바꾼다. 입력이 비어 있으면 빈 문자열로 맞춘다. */
export const toBasicInfoForm = (
  profile: StudentProfile,
): StudentBasicInfoForm => ({
  englishName: profile.englishName ?? '',
  birthday: profile.birthday ?? '',
  gender: profile.gender ?? '',
});

/**
 * 기본 정보에서 바뀐 항목만 골라 저장 요청 값을 만든다.
 * 빈 객체면 바뀐 값이 없다는 뜻이다.
 */
export const toBasicInfoRequest = (
  form: StudentBasicInfoForm,
  origin: StudentBasicInfoForm,
) => {
  const request: PutStudentBasicInfoRequest = {};
  if (form.englishName !== origin.englishName) {
    request.englishName = form.englishName ?? '';
  }
  if (form.birthday !== origin.birthday) {
    request.birthday = form.birthday ?? '';
  }
  if (form.gender !== origin.gender) {
    request.gender = form.gender as 'MALE' | 'FEMALE';
  }
  return request;
};

//============================== 보호자 정보 ==============================

/** 학생 내 정보 화면에서 다루는 보호자 한 명의 값. */
export type StudentGuardianForm = {
  name: string;
  phone: string | null;
  /** 보호자 성별 (MALE | FEMALE) */
  gender: string;
  birthday?: string;
  /** 국가 코드. 폼에서는 숫자만 다룬다 ('82' 형태). '+'는 입력창 표시와 저장 요청에서 붙인다. */
  countryCode?: string;
};

/** 보호자 정보 카드의 폼 값. */
export type StudentGuardianInfoForm = {
  /** 등록된 보호자가 없으면 null. (조회 응답의 빈 guardianInfo도 null로 맞춘다) */
  guardianInfo: StudentGuardianForm | null;
  /**
   * 새 보호자를 등록할 때 받는 개인정보 수집 · 이용 동의.
   * 보호자 입력과 함께 검증되도록 폼 값으로 다룬다.
   */
  guardianAgreed?: boolean;
};

/** 새로 등록할 보호자의 빈 입력값. */
export const EMPTY_GUARDIAN: StudentGuardianForm = {
  name: '',
  phone: '',
  gender: '',
  birthday: '',
  countryCode: DEFAULT_COUNTRY_CODE,
};

/**
 * 조회 응답의 보호자.
 * 이 화면이 다루는 보호자는 guardianInfo 하나뿐이다.
 * 교사/어드민 화면의 보호자 목록(parents)은 학생 화면에서 쓰지 않는다.
 */
const toGuardian = (profile: StudentProfile): StudentGuardianForm | null => {
  const guardianInfo = profile.guardianInfo;
  /**
   * 보호자를 등록하지 않아도 최초 로그인 시점에 빈 행이 만들어져 객체는 항상 내려온다.
   * 이름이 비어 있으면 등록된 보호자가 아니다.
   */
  if (!guardianInfo?.name) return null;

  // 미입력 항목은 null로 내려오므로 폼의 빈 값(빈 문자열)에 맞춘다.
  return {
    name: guardianInfo.name,
    phone: guardianInfo.phone ?? '',
    gender: guardianInfo.gender ?? '',
    birthday: guardianInfo.birthday ?? '',
    countryCode: toCountryCodeValue(guardianInfo.countryCode),
  };
};

/** 조회 응답을 보호자 정보 폼 값으로 바꾼다. */
export const toGuardianInfoForm = (
  profile: StudentProfile,
): StudentGuardianInfoForm => ({
  guardianInfo: toGuardian(profile),
});

/** 저장 값 정리: 연락처는 숫자만, 국가 코드는 '+82' 형태로 맞춘다. */
export const normalizeGuardian = (guardian: StudentGuardianForm | null) =>
  guardian && {
    ...guardian,
    phone: normalizePhone(guardian.phone ?? ''),
    countryCode: toCountryCodeValue(guardian.countryCode),
  };

/**
 * 보호자 정보가 바뀌었는지 확인한다.
 * 연락처는 하이픈 유무만 다를 수 있으므로 저장 형태로 맞춰서 비교한다.
 */
export const isGuardianChanged = (
  guardian: StudentGuardianForm | null,
  origin: StudentGuardianForm | null,
) =>
  JSON.stringify(normalizeGuardian(guardian)) !==
  JSON.stringify(normalizeGuardian(origin));
